/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import { AwvInfo, AwvInfoConditionEnum, ResultTaskList } from "client";
import { useUserContext } from "context/UserContext";
import { ConfirmDialog } from "components/Common/ConfirmDialog";
import { BorderedButton } from "components/Common/BorderedButton";
import { BorderedIconButton } from "components/Common/BorderedIconButton";
import { AddIcon } from "components/Common/AddIcon";
import { CameraIcon } from "components/Common/CameraIcon";
import { PositioningDialog } from "components/Tasks/PositioningDialog";
import { initializeTaskData } from "context/TaskDataProvider";
import { ErrorDialog } from "components/Common/ErrorDialog";

interface Props {
  awvInfo: AwvInfo;
  tasks?: Array<ResultTaskList>;
  newExecute: (awvInfo: AwvInfo) => void;
  goHomeExecute: (awvInfo: AwvInfo) => void;
  cancelExecute: (task: ResultTaskList) => void;
}

export const AwvControl: FC<Props> = ({
  awvInfo,
  tasks,
  newExecute,
  goHomeExecute,
  cancelExecute,
}) => {
  const { t } = useTranslation();
  const { roleName } = useUserContext();
  const navigate = useNavigate();
  const [bgColor, setBgColor] = useState<string>("white");

  const canNew =
    roleName !== "awv_operator" &&
    !(awvInfo.frameTypeAndNumber?.startsWith("empty-") ?? false);
  const handleNew = () => {
    newExecute(awvInfo);
  };

  const canCancel =
    roleName !== "awv_operator" &&
    !!awvInfo.condition &&
    awvInfo.condition !== AwvInfoConditionEnum.Offline &&
    (tasks?.some((e) => e.taskStatus === 2) ?? false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleCancel = () => {
    setConfirmOpen(true);
  };

  const canGoHome =
    roleName !== "awv_operator" &&
    !!awvInfo.condition &&
    awvInfo.condition !== AwvInfoConditionEnum.Offline &&
    (tasks?.every((e) => e.taskStatus !== 2) ?? false);
  const [goHomeOpen, setGoHomeOpen] = useState(false);
  const handleGoHome = () => {
    setGoHomeOpen(true);
  };

  const handleCamera = () => {
    navigate("/camera", { state: awvInfo });
  };

  useEffect(() => {
    if (
      !awvInfo.condition ||
      awvInfo.condition === AwvInfoConditionEnum.Offline
    ) {
      setBgColor("#E6E6E6");
    } else if (awvInfo.condition === AwvInfoConditionEnum.Error) {
      setBgColor("#FFD9D9");
    } else {
      setBgColor("white");
    }
  }, [awvInfo.condition]);

  const [isPositioningMode, setIsPositioningMode] = useState(false);
  const [selectedTask, setSelectedTask] =
    useState<ResultTaskList>(initializeTaskData);
  const [isError, setIsError] = useState(false);
  const canPositioningState = () => {
    return (
      roleName !== "awv_operator" &&
      !!awvInfo.condition &&
      awvInfo.condition !== AwvInfoConditionEnum.Offline &&
      awvInfo.condition !== AwvInfoConditionEnum.Error
    );
  };
  const canPositioning = canPositioningState();
  const handlePositioningButton = () => {
    const executingTask = tasks?.filter((task) => task.taskStatus === 2);
    if (executingTask && executingTask.length > 0) {
      setSelectedTask(executingTask[0]);
      setIsPositioningMode(true);
    } else {
      setIsError(true);
    }
  };

  return (
    <CustomContainer bg={bgColor}>
      <BorderedIconButton disabled={!canNew} onClick={handleNew}>
        <AddIcon />
      </BorderedIconButton>
      <BorderedButton disabled={!canCancel} onClick={handleCancel}>
        {t("AWVUI-MSG29")}
      </BorderedButton>
      <BorderedButton disabled={!canGoHome} onClick={handleGoHome}>
        {t("AWVUI-MSG30")}
      </BorderedButton>
      <BorderedButton
        disabled={!canPositioning}
        onClick={handlePositioningButton}
      >
        {t("AWVUI-MSG176")}
      </BorderedButton>
      <Spacer />
      <BorderedIconButton
        disabled={
          !awvInfo.condition ||
          awvInfo.condition === AwvInfoConditionEnum.Offline
        }
        onClick={handleCamera}
      >
        <CameraIcon />
      </BorderedIconButton>
      <ConfirmDialog
        open={confirmOpen}
        message={t("AWVUI-MSG08")}
        negativeButtonText={t("AWVUI-MSG12")}
        onNegativeButtonClick={() => {
          setConfirmOpen(false);
        }}
        positiveButtonText={t("AWVUI-MSG11")}
        onPositiveButtonClick={() => {
          setConfirmOpen(false);
          // find running task.
          const cancelTarget = tasks?.find((task) => task.taskStatus === 2);
          if (cancelTarget) {
            cancelExecute(cancelTarget);
          }
        }}
      />
      <ConfirmDialog
        open={goHomeOpen}
        message={t("AWVUI-MSG85")}
        negativeButtonText={t("AWVUI-MSG12")}
        onNegativeButtonClick={() => {
          setGoHomeOpen(false);
        }}
        positiveButtonText={t("AWVUI-MSG11")}
        onPositiveButtonClick={() => {
          setGoHomeOpen(false);
          goHomeExecute(awvInfo);
        }}
      />
      <PositioningDialog
        open={isPositioningMode}
        awvInfo={awvInfo}
        task={selectedTask}
        onClose={() => {
          setIsPositioningMode(false);
        }}
      />
      <ErrorDialog
        open={isError}
        onPositiveButtonClick={() => {
          setIsError(false);
        }}
        message={t("AWVUI-MSG177")}
        positiveButtonText={t("AWVUI-MSG22")}
      />
    </CustomContainer>
  );
};

interface CustomContainerProps {
  bg: string;
}

const CustomContainer = styled(Box)<CustomContainerProps>(({ theme, bg }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.66, 1),
  backgroundColor: bg,
}));

const Spacer = styled(Box)({
  flexGrow: 1,
});
